import queryString from "query-string";
import styled from "styled-components";
import Spinner from "react-bootstrap/Spinner";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, useCallback } from "react";
import {
  INSIGHTS_TYPE,
  FUNNEL_DESIGNS,
  THUMBNAIL_TYPE,
  DIGITAL_SCREENS,
  MOBILE_BACKGROUND_WIDTH
} from "./strings";
import {
  fetchSenderId,
  sendAdDetails,
  fetchSplitTestAssignment,
  fetchFormContext,
  fetchFunnelBySite,
  getUpcomingLeadById,
  fetchAMPrivacyPolicy
} from "../services/api";
import {
  setFunnel,
  setSplitTest,
  setFormContext,
  updateQA,
  updateLeadId,
  updateReminderEmailFlag,
  setLandingPageExistence,
  updateRecordActionState
} from "../store/slices/funnelInfo";
import { Greet } from "../components/Greet";
import AIInterview from "../components/AIInterview";
import { redirect } from "../services/utils";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import LandingPage from "../components/LandingPage";
import OutcomePage from "../components/OutcomePage";
import PageNotFound from "../components/PageNotFound";
import { appendScriptHtml } from "../services/helper";
import { clearCountryOption } from "../store/slices/countryOption";
import { captureOnFunnelLoad } from "../services/googleAdsAttribution";
import { ZipCodeForm } from "../components/ZipCodeForm";
import ModernVariant from "../components/ModernVariant";
import { loadOtpSession } from "../store/slices/otpSlice";
import { QuestionComponent } from "../components/Question";
import { useRecordAction } from "../hooks/useRecordAction";
import { trackTikTokViewContent } from "../services/tiktokTracking";
import OtpFlow from "../components/DigitalLeadVerification/OtpFlow";
import { ZipCodeError } from "../components/ZipCodeForm/ZipCodeError";
import { ContactInfoForm } from "../components/ContactInfoForm/ContactForm";
import AlreadyConvertedContainer from "../components/AlreadyConvertedContainer";
import { updateGlobalPrivacyPolicy } from "../store/slices/globalPrivacyPolicyInfo";

const Background = styled.div`
  height: 100vh;
  overflow: hidden;
  background-size: cover;
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-image: url(${({ $bgImage }) => $bgImage});
  font-family: "Roboto", sans-serif;
`;
const Main = styled.div`
  width: 100%;
  padding: 0px 20px;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  overflow-x: hidden;
`;
const QuestionContainer = styled.div`
  width: 580px;
  max-width: 580px;
  background-color: white;
  padding-bottom: 50px;
  border-radius: 6px;
  box-shadow: 0px 0px 2px 0px #9e9e9e;

  @media (max-width: 660px) {
    width: 100%;
  }
`;
const SpinnerContainer = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const StyledSpinner = styled(Spinner)`
  width: 60px;
  height: 60px;
`;

// Split test on the order form (Cockpit, Asana 1219217770567617): ask which
// variant this visitor gets. Never awaited — the funnel renders the control
// text when the request fails or no test runs. In test mode nothing is
// counted; ?splitTestVariant=B previews the variant.
// Order-form context (split-test elements v2): colours, social-proof count,
// Google rating. Fire-and-forget; the form falls back to its defaults.
const loadFormContext = async ({ dispatch, funnelId }) => {
  try {
    if (!funnelId) return;
    const resp = await fetchFormContext(funnelId);
    const context = resp?.data?.data;
    if (context?.colours) dispatch(setFormContext(context));
  } catch {
    // no context: default colours, no trust elements
  }
};

const loadSplitTestAssignment = async ({
  dispatch,
  funnelId,
  senderId,
  previewVariant
}) => {
  try {
    const isTestMode =
      queryString.parse(window.location.search).testMode === "true";
    if (!funnelId || (!senderId && !previewVariant)) return;
    if (isTestMode && !previewVariant) return;
    const params = { funnelId };
    if (isTestMode) {
      params.preview = previewVariant;
    } else {
      params.senderId = senderId;
    }
    const resp = await fetchSplitTestAssignment(params);
    const assignment = resp?.data?.data;
    if (assignment?.experimentId && assignment?.element) {
      dispatch(setSplitTest(assignment));
    }
  } catch {
    // no assignment: control text, nothing recorded
  }
};

const IndexPage = () => {
  const { THANKS_DIGITAL, THANKS_MANUAL } = DIGITAL_SCREENS;
  const dispatch = useDispatch();
  const { handleRecordAction, handleRemoveAction } = useRecordAction();

  // The country option lives as long as the funnel page: it is cleared when the user leaves
  useEffect(
    () => () => {
      dispatch(clearCountryOption());
    },
    [dispatch]
  );
  const zipInfo = useSelector((state) => state.zipInfo);
  const reclaimMode = useSelector((state) => state.reclaimMode);
  const funnelInfo = useSelector((state) => state.funnelInfo);
  const { funnel, doRemove, isLandingPageEnabled } = funnelInfo;

  // ── OTP screen state ─────────────────────────────────────────────────────────────
  const otpScreen = useSelector((state) => state.otp.screen);

  // ── Detect active OTP session from sessionStorage ─────────────────────────
  // On page refresh, funnelInfo.leadForm.isSubmitted resets to false (Redux in-memory).
  // But if the user was mid-OTP-flow, we can detect that from sessionStorage
  // and show OtpFlow directly without going back to the question screen.
  const [otpSession] = useState(() => loadOtpSession());
  // AI interview for funnels WITHOUT the OTP flow: shown once between
  // submission and the standard thank-you (<Greet />). The OTP path renders
  // it inside OtpFlow instead (before ThanksDigitalPage).
  const [aiInterviewDone, setAiInterviewDone] = useState(false);
  const hasActiveOtpSession =
    otpSession?.leadId &&
    otpSession?.phone &&
    otpSession?.screen &&
    otpSession.screen !== THANKS_MANUAL &&
    otpSession.screen !== THANKS_DIGITAL;

  const isTestMode =
    queryString.parse(window.location.search).testMode === "true"
      ? true
      : false;
  const leadIdParam =
    queryString.parse(window.location.search).leadId ||
    funnelInfo.leadForm.leadId;
  const [leadError, setLeadError] = useState("");
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [shouldDisplayOutcomeAtEnd, setShouldDisplayOutcomeAtEnd] =
    useState(false);
  const [isOutcomeLoading, setIsOutcomeLoading] = useState(true);
  const [isAlreadyConverted, setIsAlreadyConverted] = useState(false);
  const [upcomingLeadInfo, setUpcomingLeadInfo] = useState(null);

  const thankyouPage = reclaimMode.enabled
    ? reclaimMode.globalReclaimConfig.thankyouPage
    : funnel.thankyouPage;

  const isMobileBackground =
    window.document.body.offsetWidth <= MOBILE_BACKGROUND_WIDTH;

  const progress = Math.round(
    (funnelInfo.currentQuestion / funnelInfo.allQuestions.length) * 100
  );

  const question = funnelInfo.allQuestions.find(
    (quest) => quest?.position === funnelInfo.currentQuestion
  );

  // Standard Leadservice = leadService true, not plus, not appointment
  const isStandardLeadService =
    funnel.leadService &&
    !funnel.leadServicePlus &&
    !funnel.customerAppointmentLink &&
    !funnel.leadServiceQuestions?.length;

  const isAiInterviewAndDossierEnabled =
    funnel.customer?.isAiInterviewAndDossierEnabled ?? false;

  // Whether to show OtpFlow instead of <Greet />:
  // 1. Normal path: form just submitted this session
  // 2. Refresh path: active OTP session exists in sessionStorage
  const shouldShowOtpFlow =
    isStandardLeadService &&
    otpScreen !== THANKS_MANUAL &&
    (funnelInfo.leadForm.isSubmitted || hasActiveOtpSession);

  // ── Effective submission flag ────────────────────────────────────────────
  // funnelInfo.leadForm.isSubmitted lives only in Redux memory and resets to
  // false on every page reload. If the OTP session expired (screen ===
  // THANKS_MANUAL) and the page is then reloaded, hasActiveOtpSession is also
  // false (session screen no longer qualifies), so without this flag NEITHER
  // the OTP flow NOR <Greet /> would render — a blank screen.
  // otpSession?.leadId survives reloads via sessionStorage, so we use it as
  // a fallback signal that the lead was, at some point, submitted.
  const isLeadEffectivelySubmitted =
    funnelInfo.leadForm.isSubmitted || Boolean(otpSession?.leadId);

  const getSiteFunnel = async () => {
    try {
      let domain = "";
      let subDomain = "";
      if (
        queryString.parse(window.location.search).domain &&
        queryString.parse(window.location.search).subDomain
      ) {
        domain = queryString.parse(window.location.search).domain;
        subDomain = queryString.parse(window.location.search).subDomain;
      } else {
        const splittedHostname = window.location.hostname.split(/[,.\s]/);
        subDomain = splittedHostname[0];
        domain = `${splittedHostname[1]}.${splittedHostname[2]}`;
      }
      const serachParams = queryString.parse(window.location.search);
      // Setting fbc with visit's timestamp on behalf of fbclid
      const fbclidFromUrl = serachParams.fbclid;
      const fbclidFromStorage = localStorage.getItem("fbclid");
      if (fbclidFromUrl) {
        if (fbclidFromStorage !== fbclidFromUrl) {
          localStorage.setItem("fbc", `fb.1.${Date.now()}.${fbclidFromUrl}`);
          localStorage.setItem("fbclid", fbclidFromUrl);
        }
      } else {
        localStorage.removeItem("fbc");
        localStorage.removeItem("fbclid");
      }
      const tikTokClickIdFromUrl = serachParams.ttclid;
      if (tikTokClickIdFromUrl) {
        // Keep TikTok click id available until the visitor submits the lead.
        localStorage.setItem("ttclid", tikTokClickIdFromUrl);
      } else {
        localStorage.removeItem("ttclid");
      }
      // Google Ads click and campaign data, kept 90 days (first touch); am_*
      // values of a Google Ads visit are Google data: no Meta ad details and
      // no Facebook ad lookup for them
      const adData = captureOnFunnelLoad({ searchParams: serachParams });
      // Only the funnel request holds back the first question. The sender id
      // (tracking) is not needed for it, so first-time visitors - nearly every
      // ad click - fetch both in parallel instead of one after the other.
      const storedSenderId = localStorage.getItem("senderId");
      const [response, senderIdResponse] = await Promise.all([
        // Send TikTok click id to cockpit-service-funnel-app with the funnel load.
        fetchFunnelBySite({
          fbc: localStorage.getItem("fbc"),
          ttclid: localStorage.getItem("ttclid"),
          adData,
          domain,
          senderId: storedSenderId,
          subDomain,
          sourceUrl: window.location.href
        }),
        storedSenderId
          ? null
          : fetchSenderId().catch(() => {
              console.log("Error in fetching sender id");
              return null;
            })
      ]);
      const senderId = senderIdResponse?.data?.data?.senderId;
      if (senderId) {
        localStorage.setItem("senderId", senderId);
      }
      const data = response.data.data;
      // No live funnel for this domain (archived or unknown): render
      // <PageNotFound /> and send no tracking for the offline page.
      if (!data?._id) return;
      // Fire TikTok browser ViewContent only when the selected pixel is TikTok.
      trackTikTokViewContent(data);
      const filteredAdData = Object.entries(adData).filter(
        ([key, value]) => value !== undefined
      );
      if (filteredAdData.length) {
        // Tracking only, so it runs in the background
        sendAdDetails({ adData, funnelId: data._id }).catch(() => {
          console.log("Error in sending ad details");
        });
      }
      if (data.reclaimMode || data.companyInfo?.requirePrivacyConsent) {
        // Awaited on purpose: the consent text and its version must be there
        // before any form can render or submit (e.g. the reminder-email link
        // opens the form right away)
        try {
          const amPrivacyPolicyResp = await fetchAMPrivacyPolicy();
          const amPrivacyPolicy = amPrivacyPolicyResp.data.data;
          if (amPrivacyPolicy) {
            dispatch(updateGlobalPrivacyPolicy(amPrivacyPolicy));
          }
        } catch {
          console.log("Error in fetch global policy");
        }
      }
      const { isLandingPageEnabled, funnelQA, scriptHead, scriptBody } = data;
      dispatch(setFunnel(data));
      loadSplitTestAssignment({
        dispatch,
        funnelId: data._id,
        senderId: senderId || storedSenderId,
        previewVariant: serachParams.splitTestVariant
      });
      loadFormContext({ dispatch, funnelId: data._id });
      dispatch(
        updateRecordActionState({
          isLandingPage: true,
          ...(isLandingPageEnabled
            ? {
                nextInsightRefId: INSIGHTS_TYPE.LANDING_PAGE,
                nextInsightType: INSIGHTS_TYPE.LANDING_PAGE
              }
            : {
                nextInsightRefId: funnelQA[0]._id,
                nextInsightType: INSIGHTS_TYPE.QUESTION
              }),
          adData
        })
      );
      dispatch(setLandingPageExistence(isLandingPageEnabled));
      if (funnelQA.length) {
        dispatch(updateQA(funnelQA));
      }
      appendScriptHtml(scriptHead, document.head);
      appendScriptHtml(scriptBody, document.body);
    } catch (err) {
      console.log("Error in fetch funnel", err);
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleOutcomeLoader = () => {
    setTimeout(() => {
      setIsOutcomeLoading(false);
    }, 2000);
  };

  const getUpcomingLead = async (_leadId) => {
    try {
      const response = await getUpcomingLeadById({
        id: _leadId,
        includeDeleted: true
      });
      setUpcomingLeadInfo(response.data.data);
      setIsAlreadyConverted(response.data.data.deleted);
    } catch (error) {
      console.log("Error in getting incomplete lead ", error);
    }
  };

  const getFunnelCb = useCallback(getSiteFunnel, [dispatch]);
  const getUpcomingLeadCb = useCallback(
    (_leadId) => getUpcomingLead(_leadId),
    []
  );

  useEffect(() => {
    if (funnel.companyInfo?.companyName) {
      document.title = funnel.companyInfo.companyName;
    }
  }, [funnel]);

  useEffect(() => {
    getFunnelCb();
  }, [getFunnelCb]);

  useEffect(() => {
    if (
      funnelInfo.outcome &&
      funnelInfo.outcome?.outcomeLogic === "end" &&
      funnelInfo.allQuestions.length === funnelInfo.currentQuestion
    ) {
      setShouldDisplayOutcomeAtEnd(true);
      handleOutcomeLoader();
    }
  }, [funnelInfo]);

  useEffect(() => {
    if (
      funnelInfo.outcome?.outcomeLogic === "directly" &&
      funnel.funnelDesign === FUNNEL_DESIGNS.CLASSIC
    ) {
      handleOutcomeLoader();
    }
  }, [funnelInfo.outcome?.outcomeLogic, funnel]);

  // RECORD INSIGHT
  useEffect(() => {
    if (
      !Object.keys(funnelInfo.recordActionState)?.length ||
      isTestMode ||
      (reclaimMode.enabled &&
        funnelInfo.recordActionState.insightRefId !== INSIGHTS_TYPE.ZIP)
    ) {
      return;
    }

    handleRecordAction();
  }, [
    funnelInfo.recordActionState,
    handleRecordAction,
    isTestMode,
    reclaimMode.enabled
  ]);

  const questionToRemove = funnelInfo.allQuestions.find(
    (quest) => quest?.position === funnelInfo.questionToRemove
  );
  //REMOVE INSIGHT
  useEffect(() => {
    if (isTestMode || !doRemove) {
      return;
    }

    handleRemoveAction(questionToRemove?._id);
  }, [question, handleRemoveAction, isTestMode, doRemove, questionToRemove]);

  useEffect(() => {
    if (leadIdParam) {
      dispatch(updateLeadId(leadIdParam));
      dispatch(updateReminderEmailFlag(true));
      if (funnel.textElements?.enablePhoneWithTwoSteps) {
        getUpcomingLeadCb(leadIdParam);
      }
    }
  }, [leadIdParam, dispatch, getUpcomingLeadCb, funnel.textElements]);

  return isPageLoading ? (
    <SpinnerContainer>
      <StyledSpinner animation="border" variant="secondary" />
    </SpinnerContainer>
  ) : !funnel._id ? (
    // The store starts with `funnel: {}`, so check the id, not the object
    <PageNotFound />
  ) : isLandingPageEnabled ? (
    <LandingPage />
  ) : funnelInfo.leadForm.isSubmitted &&
    thankyouPage?.redirectLead &&
    thankyouPage?.redirectUrl ? (
    redirect({
      redirectUrl: thankyouPage.redirectUrl,
      redirectUrlParams: funnelInfo.redirectUrlParams
    })
  ) : (
    <>
      {/* test mode banner */}
      {isTestMode && (
        <div
          style={{ zIndex: "9999" }}
          className="sticky-top py-2 bg-danger text-white text-center fw-bold"
        >
          DU BEFINDEST DICH IM TEST MODUS
        </div>
      )}
      {funnel.funnelDesign === FUNNEL_DESIGNS.MODERN ? (
        <ModernVariant
          question={question}
          progress={progress}
          isTestMode={isTestMode}
        />
      ) : (
        // CLASSIC Variant
        <Background
          $bgImage={`${process.env.REACT_APP_ASSET_CDN_URL}/${
            funnel.design?.backgroundImage?.key
          }/${
            isMobileBackground
              ? THUMBNAIL_TYPE.MOBILE_BACKGROUND
              : THUMBNAIL_TYPE.BACKGROUND
          }`}
        >
          <Main>
            <Header />
            {funnelInfo.outcome.outcomeLogic === "directly" ||
            shouldDisplayOutcomeAtEnd ? (
              <OutcomePage
                outcome={funnelInfo.outcome}
                isLoading={isOutcomeLoading}
                loaderColor={funnel.design?.baseColor}
              />
            ) : isAlreadyConverted ? (
              <AlreadyConvertedContainer />
            ) : (
              <QuestionContainer>
                {funnelInfo.leadForm.isLandedFromPhoneReminderEmail &&
                funnel.textElements?.enablePhoneWithTwoSteps ? (
                  <ContactInfoForm
                    setLeadError={setLeadError}
                    upcomingLeadInfo={upcomingLeadInfo}
                  />
                ) : (
                  <>
                    {/* ── Step 1: Questions ──────────────────────────────── */}
                    {funnelInfo.currentQuestion <
                      funnelInfo.allQuestions.length &&
                      !isLeadEffectivelySubmitted &&
                      !hasActiveOtpSession && (
                        <QuestionComponent
                          progress={progress}
                          questionData={question}
                        />
                      )}

                    {/* ── Step 2: Zip code ───────────────────────────────── */}
                    {funnelInfo.currentQuestion ===
                      funnelInfo.allQuestions.length &&
                      !zipInfo.isSubmitted &&
                      !isLeadEffectivelySubmitted &&
                      !hasActiveOtpSession && <ZipCodeForm />}

                    {/* ── Step 3: Contact form ───────────────────────────── */}
                    {zipInfo.isSubmitted &&
                      !isLeadEffectivelySubmitted &&
                      !hasActiveOtpSession &&
                      (zipInfo.filterData?.shouldCreate ||
                      reclaimMode.enabled ? (
                        <ContactInfoForm setLeadError={setLeadError} />
                      ) : (
                        <ZipCodeError />
                      ))}

                    {/* ── Step 4a: OTP flow (Standard Leadservice) ──────── */}
                    {shouldShowOtpFlow && <OtpFlow />}
                    {/* ── Step 4b: Standard thank-you (all other types, or
                            after manual fallback / session expiry from OTP) ── */}
                    {isLeadEffectivelySubmitted &&
                      !thankyouPage?.redirectLead &&
                      !shouldShowOtpFlow &&
                      (funnelInfo.leadForm.leadId &&
                      !leadError &&
                      isAiInterviewAndDossierEnabled &&
                      !aiInterviewDone ? (
                        <AIInterview
                          leadId={funnelInfo.leadForm.leadId}
                          onFinish={() => setAiInterviewDone(true)}
                        />
                      ) : (
                        <Greet leadError={leadError} />
                      ))}
                  </>
                )}
              </QuestionContainer>
            )}
            <Footer />
          </Main>
        </Background>
      )}
    </>
  );
};

export default IndexPage;
